/**
 * PATH: packages/ui/src/components/header/BackButton.tsx
 * FILE_ID: UI-HEADER-BACKBUTTON-001
 * PURPOSE: Global "Back" affordance rendered in the shared Header top-left so
 *   EVERY hub using DashboardLayout gets a consistent back without per-page
 *   wiring.
 * WHY: operator 2026-06-29 — "add a back option throughout the app … go back to
 *   the page/screen where you came from".
 * FLOW:
 *   (mounted persistently in Header)
 *   useTrackInAppNav() ─▶ keeps in-app depth current across navigations
 *   breadcrumbs ─ parentFallbackHref ─┐
 *                                     ├─ useSmartBack ─▶ <button> ChevronLeft
 *   in-app depth ─ canGoBack ─────────┘
 * DEPENDENCIES:
 *   - ../../navigation/useSmartBack: depth tracking + smart goBack
 *   - react-router-dom (useInRouterContext): guard for non-router hosts
 */
import { ChevronLeft } from 'lucide-react'
import { useInRouterContext, useLocation } from 'react-router-dom'
import { cn } from '../../lib/utils'
import { hrefPointsAt, useSmartBack, useTrackInAppNav } from '../../navigation/useSmartBack'
import type { BreadcrumbItem } from '../types'

export interface HeaderBackButtonProps {
  breadcrumbs?: BreadcrumbItem[]
  className?: string
}

/**
 * Nearest linkable in-app parent from the breadcrumb trail (the crumb before
 * the current one). Used as the fallback target when there is no in-app history
 * to pop (deep-link / first screen) so the button is still meaningful and never
 * escapes the SPA. Skips external (http) crumbs — back must stay in-app.
 */
function parentFallbackHref(
  breadcrumbs: BreadcrumbItem[] | undefined,
  pathname: string,
  search: string,
): string | undefined {
  if (!breadcrumbs || breadcrumbs.length < 2) return undefined
  for (let i = breadcrumbs.length - 2; i >= 0; i--) {
    const href = breadcrumbs[i]?.href
    if (href && !href.startsWith('http') && !hrefPointsAt(href, pathname, search)) return href
  }
  return undefined
}

/**
 * Router-context guard: in non-router hosts (e.g. isolated Storybook stories)
 * this silently renders nothing instead of throwing on the router hooks — the
 * same effective behaviour the Header had before this control existed.
 */
export function HeaderBackButton(props: HeaderBackButtonProps) {
  if (!useInRouterContext()) return null
  return <HeaderBackButtonInner {...props} />
}

function HeaderBackButtonInner({ breadcrumbs, className }: HeaderBackButtonProps) {
  // Drive the shared depth from this persistent header mount. Runs every render
  // of the (always-mounted) header so depth stays accurate even while the
  // button itself is hidden below.
  useTrackInAppNav()

  const location = useLocation()
  const fallback = parentFallbackHref(breadcrumbs, location.pathname, location.search)
  const { canGoBack, goBack } = useSmartBack(fallback)

  // Top-level / fresh screen with nowhere to go: render nothing rather than a
  // dead control. (The component stays mounted, so tracking continues.)
  if (!canGoBack && !fallback) return null

  return (
    <button
      type="button"
      onClick={goBack}
      aria-label="Go back"
      title="Go back"
      className={cn(
        'flex items-center justify-center p-1.5 -ml-1 shrink-0 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted',
        className,
      )}
    >
      <ChevronLeft className="w-5 h-5" />
    </button>
  )
}

export default HeaderBackButton
